import { Fragment, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  useApi,
  discoveryApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { MarkdownContent } from '@backstage/core-components';
import {
  Box,
  Typography,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import GetAppIcon from '@material-ui/icons/GetApp';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import EditIcon from '@material-ui/icons/Edit';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import BuildIcon from '@material-ui/icons/Build';
import SpeedIcon from '@material-ui/icons/Speed';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import { useMarketplace } from './MarketplaceContext';
import { TYPE_META, HERO_GRADIENT, isComingSoonType, isWindsurfSupportedType } from './typeMetadata';
import { TypeIcon } from './TypeIcon';
import { ItemType, MarketplaceItem } from './types';
import { InstallModal } from './InstallModal';
import { ContributeDialog } from './ContributeDialog';
import { CommentsSection } from './CommentsSection';
import { MARKETPLACE_ADMIN_EMAILS } from './marketplace-admins';
import { useMarketplaceFetchApi } from './marketplaceFetch';

// All known item types — used to validate the URL query param before indexing TYPE_META.
const VALID_TYPES: ItemType[] = [
  'agent',
  'skill',
  'prompt',
  'rule',
  'template',
  'model',
  'mcp-server',
  'playbook',
];

// HERO_GRADIENT is imported from ./typeMetadata to avoid a circular import
// (CommentsSection → ItemDetailView → CommentsSection).

// Maturity badge colours — keyed by maturity string.
const MATURITY_STYLE: Record<string, { bg: string; color: string }> = {
  production: { bg: '#E8F5E9', color: '#2E7D32' },
  beta: { bg: '#FFF3E0', color: '#E65100' },
  experimental: { bg: '#E3F2FD', color: '#1565C0' },
};

const useStyles = makeStyles((theme: Theme) => ({
  hero: {
    padding: theme.spacing(4, 5, 4.5),
    color: '#fff',
    // Subtle inner bottom line to visually separate hero from metadata row.
    boxShadow: 'inset 0 -1px 0 rgba(255,255,255,0.08)',
  },
  backBtn: {
    color: 'rgba(255,255,255,0.65)',
    textTransform: 'none',
    fontWeight: 500,
    marginBottom: theme.spacing(2.5),
    padding: '2px 0',
    minWidth: 0,
    '&:hover': { color: '#fff', background: 'none' },
  },
  heroRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2.5),
    flexWrap: 'wrap',
  },
  avatar: {
    width: 64,
    height: 64,
    borderRadius: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '2rem',
    flexShrink: 0,
    // White-tinted background works on every gradient colour.
    background: 'rgba(255,255,255,0.12)',
    border: '1.5px solid rgba(255,255,255,0.18)',
  },
  heroTitle: {
    fontWeight: 800,
    fontSize: '2.1rem',
    lineHeight: 1.15,
    color: '#fff',
    letterSpacing: '-0.3px',
  },
  heroBadgeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.25),
    flexWrap: 'wrap',
  },
  heroDesc: {
    color: 'rgba(255,255,255,0.72)',
    fontSize: '0.95rem',
    lineHeight: 1.6,
    marginTop: theme.spacing(1.75),
    maxWidth: 680,
  },
  // Wrapper for metadata + tabs + content.
  body: {
    // No maxWidth — let metaRow and tabsBar stretch full width.
    overflow: 'hidden',
  },
  metaRow: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    padding: theme.spacing(2.5, 5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
  },
  metaCard: {
    background: theme.palette.action.hover,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: theme.spacing(1.25, 3),
  },
  metaLabel: {
    fontSize: '0.62rem',
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    color: theme.palette.text.secondary,
    fontWeight: 700,
    marginBottom: 2,
  },
  metaValue: {
    fontSize: '0.9rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  // Tab bar sits on a bottom border so the active indicator floats cleanly.
  tabsBar: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingLeft: theme.spacing(5),
    background: theme.palette.background.paper,
  },
  tabContent: {
    padding: theme.spacing(4, 5),
  },
  errorBlock: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 8,
    background: theme.palette.error.light,
    color: theme.palette.error.contrastText,
    fontSize: '0.85rem',
  },
  infoBlock: {
    padding: theme.spacing(1.25, 1.75),
    borderRadius: 8,
    background:
      theme.palette.type === 'dark' ? 'rgba(255,255,255,0.08)' : '#EFF6FF',
    border: `1px solid ${theme.palette.type === 'dark' ? 'rgba(255,255,255,0.12)' : '#BFDBFE'}`,
    color: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.88)' : '#1D4ED8',
    fontSize: '0.85rem',
    marginTop: theme.spacing(1.5),
    maxWidth: 520,
  },
  notFound: {
    padding: theme.spacing(6, 5),
    textAlign: 'center',
  },
  githubBtnRoot: {
    minHeight: '24px !important',
    height: '24px !important',
    padding: '0 8px !important',
    lineHeight: 1,
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'none',
    color: 'rgba(255,255,255,0.85)',
    background: 'rgba(255,255,255,0.12)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 12,
    marginBottom: 10,
  },
  githubBtnLabel: {
    display: 'flex',
    alignItems: 'center',
  },
  // ── Action button row ──
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2.5),
  },
  // Thin vertical rule that separates primary from owner actions
  actionDivider: {
    width: 1,
    height: 28,
    background: 'rgba(255,255,255,0.18)',
    borderRadius: 1,
    margin: '0 4px',
  },
  // Ghost buttons on the dark hero
  btnEdit: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.8rem',
    padding: '5px 14px',
    borderRadius: 7,
    color: 'rgba(255,255,255,0.82)',
    borderColor: 'rgba(255,255,255,0.25)',
    '&:hover': {
      background: 'rgba(255,255,255,0.08)',
      borderColor: 'rgba(255,255,255,0.45)',
      color: '#fff',
    },
    '&.Mui-disabled': {
      color: 'rgba(255,255,255,0.28)',
      borderColor: 'rgba(255,255,255,0.12)',
    },
  },
  btnDelete: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.8rem',
    padding: '5px 14px',
    borderRadius: 7,
    color: '#FF8A8A',
    borderColor: 'rgba(255,138,138,0.4)',
    '&:hover': {
      background: 'rgba(255,100,100,0.1)',
      borderColor: 'rgba(255,138,138,0.7)',
      color: '#FFAAAA',
    },
  },
  btnMaintenance: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.8rem',
    padding: '5px 14px',
    borderRadius: 7,
    color: '#FFB347',
    borderColor: 'rgba(255,179,71,0.4)',
    '&:hover': {
      background: 'rgba(255,167,38,0.1)',
      borderColor: 'rgba(255,179,71,0.7)',
      color: '#FFC875',
    },
    '&.Mui-disabled': {
      color: 'rgba(255,179,71,0.3)',
      borderColor: 'rgba(255,179,71,0.15)',
    },
  },
  btnRestore: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.8rem',
    padding: '5px 14px',
    borderRadius: 7,
    color: '#81C784',
    borderColor: 'rgba(129,199,132,0.4)',
    '&:hover': {
      background: 'rgba(100,200,120,0.1)',
      borderColor: 'rgba(129,199,132,0.7)',
      color: '#A5D6A7',
    },
    '&.Mui-disabled': {
      color: 'rgba(129,199,132,0.3)',
      borderColor: 'rgba(129,199,132,0.15)',
    },
  },
  // Maintenance PR pending banner
  maintenancePrBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.25, 1.75),
    borderRadius: 8,
    background: 'rgba(255,179,71,0.07)',
    border: '1px solid rgba(255,179,71,0.28)',
    maxWidth: 480,
  },
  maintenancePrLink: {
    color: '#FFB347',
    fontWeight: 700,
    fontSize: '0.78rem',
    whiteSpace: 'nowrap' as const,
    textDecoration: 'underline',
    flexShrink: 0,
    '&:hover': { color: '#FFC875' },
  },
}));

// ─── Markdown content loader ──────────────────────────────────────────────────

function useItemContent(item: MarketplaceItem) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useMarketplaceFetchApi();
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Prefer inline content when available — skip the network entirely.
    if (item.inlineContent) {
      setContent(item.inlineContent);
      return;
    }
    // No content file means we render metadata only.
    if (!item.rawBaseUrl || !item.contentFile) {
      setContent('');
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('marketplace');
        // Route through the backend proxy which validates the GitHub URL server-side.
        const url = `${baseUrl}/raw?url=${encodeURIComponent(`${item.rawBaseUrl}/${item.contentFile}`)}`;
        const resp = await fetchApi.fetch(url);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const text = await resp.text();
        // Strip leading YAML frontmatter block (--- ... ---) before rendering.
        const stripped = text.replace(/^---\n[\s\S]*?\n---\n?/, '');
        if (!cancelled) setContent(stripped);
      } catch (e: any) {
        if (!cancelled) setError(e.message ?? 'Failed to load content');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    item.id,
    item.rawBaseUrl,
    item.contentFile,
    item.inlineContent,
    discoveryApi,
    fetchApi,
  ]);

  return { content, loading, error };
}

function formatDisplayDate(value?: string): string {
  if (!value) return 'Unknown';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ─── Detail view (rendered when query params identify a valid item) ──────────

function DetailBody({ item }: { item: MarketplaceItem }) {
  const classes = useStyles();
  const navigate = useNavigate();
  const meta = TYPE_META[item.type];
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useMarketplaceFetchApi();
  const identityApi = useApi(identityApiRef);
  const { content, loading: mdLoading, error: mdError } = useItemContent(item);
  const [tab, setTab] = useState(0);
  const [installMenuAnchor, setInstallMenuAnchor] =
    useState<null | HTMLElement>(null);
  // Shown below the action buttons when a download attempt fails.
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [installModalOpen, setInstallModalOpen] = useState(false);
  const [backendBaseUrl, setBackendBaseUrl] = useState<string | undefined>();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletePrUrl, setDeletePrUrl] = useState<string | null>(null);
  const [userRef, setUserRef] = useState<string | undefined>();
  // Full Backstage entity ref (e.g. "user:default/jane.doe") — passed to CommentsSection
  const [currentUserEntityRef, setCurrentUserEntityRef] = useState<string>('');
  const [isCommentAdmin, setIsCommentAdmin] = useState(false);
  const [maintenanceSubmitting, setMaintenanceSubmitting] = useState(false);
  const [maintenanceError, setMaintenanceError] = useState<string | null>(null);
  const [maintenancePrUrl, setMaintenancePrUrl] = useState<string | null>(null);
  const [maintenancePrLoading, setMaintenancePrLoading] = useState(false);
  const [reactionUpCount, setReactionUpCount] = useState(item.upCount ?? 0);
  const [reactionDownCount, setReactionDownCount] = useState(item.downCount ?? 0);

  useEffect(() => {
    Promise.all([
      identityApi.getBackstageIdentity(),
      identityApi.getProfileInfo(),
    ]).then(([identity, profile]) => {
      const ref = identity.userEntityRef; // e.g. "user:default/john.doe"
      setUserRef(ref?.split('/').pop());
      setCurrentUserEntityRef(ref ?? '');
      setIsCommentAdmin(MARKETPLACE_ADMIN_EMAILS.has((profile.email ?? '').toLowerCase()));
    }).catch(() => {});
  }, [identityApi]);

  useEffect(() => {
    discoveryApi
      .getBaseUrl('marketplace')
      .then(setBackendBaseUrl)
      .catch(() => {});
  }, [discoveryApi]);

  useEffect(() => {
    if (!backendBaseUrl) return;
    fetchApi
      .fetch(`${backendBaseUrl}/reactions/${encodeURIComponent(item.name)}?type=${encodeURIComponent(item.type)}`)
      .then(r => r.json())
      .then((data: any) => {
        if (typeof data.upCount === 'number') setReactionUpCount(data.upCount);
        if (typeof data.downCount === 'number') setReactionDownCount(data.downCount);
      })
      .catch(() => {});
  }, [backendBaseUrl, item.name, item.type, fetchApi]);

  // Normalize an identity string so Backstage userRef ("john.doe") matches
  // manifest author ("john-doe_verint"): strip _org suffix, lowercase,
  // then treat dots and hyphens as equivalent.
  const normalizeIdentity = (s: string) =>
    s.replace(/_[a-z0-9]+$/i, '').replace(/[.\-]/g, '').toLowerCase();

  const canEdit = !!(userRef && item.author && normalizeIdentity(userRef) === normalizeIdentity(item.author));

  // Auto-fetch any open maintenance PR as soon as we know the user is the author
  useEffect(() => {
    if (!canEdit || !backendBaseUrl) return;
    setMaintenancePrLoading(true);
    fetchApi
      .fetch(`${backendBaseUrl}/maintenance-pr?type=${encodeURIComponent(item.type)}&id=${encodeURIComponent(item.id)}`)
      .then(r => r.json())
      .then((data: any) => {
        if (data?.prUrl) setMaintenancePrUrl(data.prUrl);
      })
      .catch(() => {})
      .finally(() => setMaintenancePrLoading(false));
  }, [canEdit, backendBaseUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async () => {
    if (!canEdit || deleteSubmitting) return;
    setDeleteSubmitting(true);
    setDeleteError(null);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('marketplace');
      const resp = await fetchApi.fetch(`${baseUrl}/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: item.type, id: item.id }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.error || `Delete failed (HTTP ${resp.status})`);
      }
      setDeletePrUrl(data?.prUrl ?? null);
      setDeleteDialogOpen(false);
    } catch (e: any) {
      setDeleteError(e.message ?? 'Delete failed. Please try again.');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleToggleMaintenance = async () => {
    if (!canEdit || maintenanceSubmitting) return;
    setMaintenanceSubmitting(true);
    setMaintenanceError(null);
    setMaintenancePrUrl(null);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('marketplace');
      const resp = await fetchApi.fetch(`${baseUrl}/toggle-maintenance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: item.type, id: item.id }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        // 409: a PR already exists — just surface its link, no error message needed
        if (resp.status === 409 && data?.prUrl) {
          setMaintenancePrUrl(data.prUrl);
        } else {
          throw new Error(data?.error || `Toggle maintenance failed (HTTP ${resp.status})`);
        }
      } else {
        setMaintenancePrUrl(data?.prUrl ?? null);
      }
    } catch (e: any) {
      setMaintenanceError(e.message ?? 'Toggle maintenance failed. Please try again.');
    } finally {
      setMaintenanceSubmitting(false);
    }
  };

  const isMaintenance = item.status === 'maintenance';

  // True when the item has enough metadata to build a VS Code deep-link.
  const canInstall = !!(
    item.rawBaseUrl &&
    (item.agentMdFile || item.contentFile)
  );
  const isComingSoon = isComingSoonType(item.type);
  const updatedAtValue = item.updatedAt ?? item.lastUpdated;
  const createdAtValue = item.createdAt ?? updatedAtValue;
  const createdLabel = formatDisplayDate(createdAtValue);
  const updatedLabel = formatDisplayDate(updatedAtValue);

  // Downloads the item as a zip via the backend proxy; never opens external URLs directly.
  const download = async (ideHint?: string) => {
    setDownloadError(null);
    if (!item.rawBaseUrl || !item.contentFile) {
      // No downloadable content registered for this item.
      setDownloadError('No downloadable content available for this item.');
      return;
    }
    try {
      const baseUrl = await discoveryApi.getBaseUrl('marketplace');
      const params = new URLSearchParams({
        rawBaseUrl: item.rawBaseUrl,
        contentFile: item.contentFile,
      });
      // Backend validates the GitHub URL and packages the file using JSZip.
      const resp = await fetchApi.fetch(`${baseUrl}/zip?${params}`);
      if (!resp.ok) throw new Error(`Server returned ${resp.status}`);
      const blob = await resp.blob();
      const folderName = item.rawBaseUrl.split('/').pop() ?? item.name;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${folderName}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      // Track download (fire-and-forget)
      fetchApi.fetch(`${baseUrl}/downloads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          artifact_name: folderName,
          artifact_type: item.type,
          ide: ideHint || 'zip',
          user_ref: userRef || 'anonymous',
        }),
      }).catch(() => {});
    } catch (e: any) {
      // Surface the error — do not fall back to opening an external URL.
      setDownloadError(e.message ?? 'Download failed. Please try again.');
    }
  };

  const maturityStyle = item.maturity
    ? (MATURITY_STYLE[item.maturity.toLowerCase()] ??
      MATURITY_STYLE.experimental)
    : null;

  // Metadata fields — only render rows that have a non-empty value.
  const metaFields: Array<{ label: string; value: React.ReactNode }> = [
    { label: 'Published', value: createdLabel },
    { label: 'Updated', value: updatedLabel },
    ...(item.author ? [{ label: 'Author', value: item.author }] : []),
    { label: 'Version (manifest)', value: item.version },
    { label: 'Type', value: meta.label },
    ...((item.downloadCount ?? 0) > 0 ? [{ label: 'Downloads', value: String(item.downloadCount) }] : []),
    /* Reactions are shown in the hero badge row instead of here */
    ...(item.provider ? [{ label: 'Provider', value: item.provider }] : []),
    ...(item.maturity ? [{ label: 'Maturity', value: item.maturity }] : []),
    ...(item.keywords.length
      ? [{ label: 'Keywords', value: item.keywords.join(', ') }]
      : []),
  ];

  return (
    <Box>
      {/* ── Hero ── */}
      <Box
        style={{ background: HERO_GRADIENT[item.type] }}
        className={classes.hero}
      >
        {/* Back button — navigate(-1) keeps browser history; fall back to list page. */}
        <Button
          className={classes.backBtn}
          startIcon={<ArrowBackIcon fontSize="small" />}
          onClick={() => navigate('/ai-marketplace')}
          disableRipple
        >
          Back to Marketplace
        </Button>

        <Box className={classes.heroRow}>
          {/* Avatar — white-tinted so it reads clearly on every gradient */}
          <Box className={classes.avatar}>
            <TypeIcon
              iconName={meta.iconName}
              style={{ color: '#fff', fontSize: '2rem' }}
            />
          </Box>

          <Box flex={1}>
            <Typography className={classes.heroTitle}>
              {item.displayName}
            </Typography>
            <Box
              className={classes.heroBadgeRow}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flexWrap: 'wrap',
              }}
            >
              {/* Type chip */}
              <Chip
                size="small"
                label={meta.label}
                style={{
                  background: `${meta.color}33`,
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                }}
              />
              {/* Version badge */}
              <Chip
                size="small"
                label={`v${item.version}`}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  color: '#fff',
                  fontSize: '0.7rem',
                }}
              />
              {/* Optional maturity badge */}
              {maturityStyle && (
                <Chip
                  size="small"
                  label={item.maturity}
                  style={{
                    background: maturityStyle.bg,
                    color: maturityStyle.color,
                    fontWeight: 700,
                    fontSize: '0.7rem',
                  }}
                />
              )}
              {/* Token-Efficient badge */}
              {item.tokenEfficient === true && (
                <Tooltip title={`Token Efficiency Score: ${item.tokenScore ?? '—'}/100`}>
                  <Chip
                    size="small"
                    icon={<SpeedIcon style={{ fontSize: 13, color: '#09A761' }} />}
                    label="Token-Efficient"
                    style={{
                      background: 'rgba(9,167,97,0.09)',
                      color: '#09A761',
                      border: '1px solid rgba(9,167,97,0.22)',
                      fontWeight: 700,
                      fontSize: '0.7rem',
                    }}
                  />
                </Tooltip>
              )}
              {/* Maintenance badge */}
              {isMaintenance && (
                <Chip
                  size="small"
                  icon={<BuildIcon style={{ color: '#E65100', fontSize: 14 }} />}
                  label="Under Maintenance"
                  style={{
                    background: '#FFF3E0',
                    color: '#E65100',
                    fontWeight: 700,
                    fontSize: '0.7rem',
                  }}
                />
              )}
              {/* GitHub link */}
              <Button
                size="small"
                href={item.repoUrl}
                target="_blank"
                rel="noopener noreferrer"
                component="a"
                endIcon={<OpenInNewIcon fontSize="small" />}
                classes={{
                  root: classes.githubBtnRoot,
                  label: classes.githubBtnLabel,
                }}
              >
                View on GitHub
              </Button>

              {/* Reaction counts — inline in the hero area */}
              {(reactionUpCount + reactionDownCount) > 0 && (
                <Box component="span" style={{ display: 'inline-flex', alignItems: 'center', gap: 14, marginLeft: 12 }}>
                  <Box component="span" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 700, fontSize: '0.78rem' }}>
                    <ThumbUpIcon style={{ fontSize: 16, color: '#90CAF9', marginBottom: 13 }} />
                    <span style={{ color: 'rgba(255,255,255,0.95)', marginBottom: 13 }}>{reactionUpCount}</span>
                  </Box>
                  <Box component="span" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 700, fontSize: '0.78rem' }}>
                    <ThumbDownIcon style={{ fontSize: 16, color: '#EF9A9A', marginBottom: 13 }} />
                    <span style={{ color: 'rgba(255,255,255,0.95)', marginBottom: 13 }}>{reactionDownCount}</span>
                  </Box>
                </Box>
              )}
            </Box>
            <Typography className={classes.heroDesc}>
              {item.description}
            </Typography>
            <Typography style={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.86rem', marginTop: 8 }}>
              Published: {createdLabel} {'\u00B7'} Updated: {updatedLabel}
            </Typography>

            {/* ── Action buttons ── */}
            <Box className={classes.actionRow}>

              {/* ─ PRIMARY: Install / Download / Under Maintenance ─ */}
              {isComingSoon && (
                <Button
                  size="small"
                  variant="contained"
                  disabled
                  style={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', borderRadius: 7 }}
                >
                  Coming soon
                </Button>
              )}

              {!isComingSoon && canInstall && !isMaintenance && (
                <>
                  <ButtonGroup size="small" variant="contained" style={{ borderRadius: 7 }}>
                    <Button
                      onClick={() => setInstallModalOpen(true)}
                      startIcon={<GetAppIcon style={{ fontSize: 15 }} />}
                      style={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', borderRadius: '7px 0 0 7px' }}
                    >
                      Install in VS Code
                    </Button>
                    <Button
                      size="small"
                      style={{ minWidth: 28, padding: '0 4px', borderRadius: '0 7px 7px 0' }}
                      onClick={e => setInstallMenuAnchor(e.currentTarget)}
                      aria-label="Select IDE"
                    >
                      <ArrowDropDownIcon fontSize="small" />
                    </Button>
                  </ButtonGroup>
                  <Menu
                    anchorEl={installMenuAnchor}
                    open={Boolean(installMenuAnchor)}
                    onClose={() => setInstallMenuAnchor(null)}
                    getContentAnchorEl={null}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <MenuItem onClick={() => { setInstallModalOpen(true); setInstallMenuAnchor(null); }}>VS Code</MenuItem>
                    <MenuItem onClick={() => { setInstallModalOpen(true); setInstallMenuAnchor(null); }}>VS Code Insiders</MenuItem>
                    {isWindsurfSupportedType(item.type) && (
                      <MenuItem onClick={() => { setInstallModalOpen(true); setInstallMenuAnchor(null); }}>Windsurf</MenuItem>
                    )}
                    <MenuItem onClick={() => { download('intellij'); setInstallMenuAnchor(null); }}>IntelliJ IDEA (zip)</MenuItem>
                  </Menu>
                </>
              )}

              {!isComingSoon && !canInstall && !isMaintenance && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<GetAppIcon style={{ fontSize: 15 }} />}
                  onClick={() => download()}
                  style={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', borderRadius: 7 }}
                >
                  Download
                </Button>
              )}

              {!isComingSoon && isMaintenance && (
                <Tooltip title="This item is under maintenance — installs are temporarily disabled" arrow>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled
                      startIcon={<BuildIcon style={{ fontSize: 14 }} />}
                      style={{
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        opacity: 0.6,
                        borderRadius: 7,
                        color: 'rgba(255,255,255,0.82)',
                        borderColor: 'rgba(255,255,255,0.25)',
                        padding: '5px 13px',
                      }}
                    >
                      Under Maintenance
                    </Button>
                  </span>
                </Tooltip>
              )}

              {/* ─ DIVIDER (only for owners) ─ */}
              {canEdit && <Box className={classes.actionDivider} />}

              {/* ─ OWNER ACTIONS: Edit / Delete / Maintenance ─ */}
              {canEdit && (
                <>
                  <Tooltip title="Edit this item" arrow>
                    <Button
                      size="small"
                      variant="outlined"
                      className={classes.btnEdit}
                      startIcon={<EditIcon style={{ fontSize: 14 }} />}
                      onClick={() => setEditDialogOpen(true)}
                    >
                      Edit
                    </Button>
                  </Tooltip>

                  <Tooltip title="Delete this item permanently" arrow>
                    <Button
                      size="small"
                      variant="outlined"
                      className={classes.btnDelete}
                      startIcon={<DeleteOutlineIcon style={{ fontSize: 14 }} />}
                      onClick={() => { setDeletePrUrl(null); setDeleteError(null); setDeleteDialogOpen(true); }}
                    >
                      Delete
                    </Button>
                  </Tooltip>

                  <Tooltip
                    title={
                      maintenancePrUrl
                        ? 'A maintenance PR is already open — merge or close it first'
                        : isMaintenance
                          ? 'Restore this item to active status'
                          : 'Put this item into maintenance mode — installs will be disabled'
                    }
                    arrow
                  >
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        className={isMaintenance ? classes.btnRestore : classes.btnMaintenance}
                        startIcon={
                          maintenanceSubmitting
                            ? <CircularProgress size={13} style={{ color: 'inherit' }} />
                            : <BuildIcon style={{ fontSize: 14 }} />
                        }
                        onClick={handleToggleMaintenance}
                        disabled={maintenanceSubmitting || !!maintenancePrUrl}
                      >
                        {maintenanceSubmitting
                          ? 'Creating PR…'
                          : isMaintenance
                            ? 'Restore Active'
                            : 'Mark Maintenance'}
                      </Button>
                    </span>
                  </Tooltip>
                </>
              )}

              {/* Edit — non-owners see a disabled ghost */}
              {!canEdit && (
                <Tooltip title="Only the author can edit this item" arrow>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      className={classes.btnEdit}
                      startIcon={<EditIcon style={{ fontSize: 14 }} />}
                      disabled
                    >
                      Edit
                    </Button>
                  </span>
                </Tooltip>
              )}

            </Box>

            {/* ─ Feedback banners ─ */}
            {isComingSoon && (
              <Box className={classes.infoBlock} style={{ marginTop: 14 }}>
                Self-service support for this artifact type is coming soon.
                Users can review the details and source on GitHub for now.
              </Box>
            )}
            {downloadError && (
              <Box className={classes.errorBlock} style={{ marginTop: 12 }}>
                {downloadError}
              </Box>
            )}
            {deleteError && (
              <Box className={classes.errorBlock} style={{ marginTop: 12 }}>
                {deleteError}
              </Box>
            )}
            {deletePrUrl && (
              <Box className={classes.infoBlock} style={{ marginTop: 12 }}>
                Delete PR created.{' '}
                <Link href={deletePrUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                  View Pull Request
                </Link>
              </Box>
            )}
            {maintenanceError && (
              <Box className={classes.errorBlock} style={{ marginTop: 12 }}>
                {maintenanceError}
              </Box>
            )}

            {/* Maintenance PR banner — auto-shown when an open PR is detected */}
            {maintenancePrLoading && canEdit && (
              <Box
                display="flex"
                alignItems="center"
                style={{
                  gap: 8,
                  marginTop: 14,
                  padding: '8px 14px',
                  borderRadius: 8,
                  background: 'rgba(255,179,71,0.07)',
                  border: '1px solid rgba(255,179,71,0.22)',
                  maxWidth: 480,
                }}
              >
                <CircularProgress size={13} style={{ color: '#FFB347', flexShrink: 0 }} />
                <Typography variant="caption" style={{ color: 'rgba(255,179,71,0.8)' }}>
                  Checking for open maintenance PRs…
                </Typography>
              </Box>
            )}

            {!maintenancePrLoading && maintenancePrUrl && (
              <Box className={classes.maintenancePrBanner}>
                <Box display="flex" alignItems="center" style={{ gap: 8, minWidth: 0 }}>
                  <BuildIcon style={{ fontSize: 15, color: '#FFB347', flexShrink: 0 }} />
                  <Typography variant="caption" style={{ color: 'rgba(255,179,71,0.9)', fontWeight: 600 }}>
                    A maintenance PR is pending review
                  </Typography>
                </Box>
                <Link
                  href={maintenancePrUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.maintenancePrLink}
                >
                  View PR →
                </Link>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* ── Metadata row, tabs, content — shared maxWidth container ── */}
      <Box className={classes.body}>
        {/* ── Metadata row ── */}
        <Box className={classes.metaRow}>
          {metaFields.map(f => (
            <Box key={f.label} className={classes.metaCard}>
              <Typography className={classes.metaLabel}>{f.label}</Typography>
              <Typography className={classes.metaValue}>{f.value}</Typography>
            </Box>
          ))}
        </Box>

        {/* ── Tabs ── */}
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
          className={classes.tabsBar}
        >
          <Tab label="Overview" />
          <Tab label="Details" />
          <Tab label="Comments" />
        </Tabs>

        <Box className={classes.tabContent}>
          {/* Overview tab — renders the markdown content file */}
          {tab === 0 && (
            <>
              {mdLoading && <CircularProgress size={20} />}
              {mdError && (
                <Box className={classes.errorBlock}>
                  Failed to load content: {mdError}
                </Box>
              )}
              {!mdLoading && !mdError && content && (
                <MarkdownContent content={content} />
              )}
              {!mdLoading && !mdError && !content && (
                <Typography
                  color="textSecondary"
                  style={{ fontSize: '0.9rem' }}
                >
                  No content available for this item.
                </Typography>
              )}
            </>
          )}

          {/* Comments tab */}
          {tab === 2 && (
            <CommentsSection item={item} currentUserRef={currentUserEntityRef} isAdmin={isCommentAdmin} />
          )}

          {/* Details tab — all non-empty metadata fields as a simple grid */}
          {tab === 1 && (
            <Box
              display="grid"
              style={{ gridTemplateColumns: '160px 1fr', gap: '10px 24px' }}
            >
              {[
                { label: 'ID', value: item.id },
                { label: 'Name', value: item.name },
                { label: 'Display Name', value: item.displayName },
                { label: 'Type', value: item.type },
                { label: 'Published', value: createdLabel },
                { label: 'Updated', value: updatedLabel },
                { label: 'Version (manifest)', value: item.version },
                ...(item.author
                  ? [{ label: 'Author', value: item.author }]
                  : []),
                ...(item.provider
                  ? [{ label: 'Provider', value: item.provider }]
                  : []),
                ...(item.maturity
                  ? [{ label: 'Maturity', value: item.maturity }]
                  : []),
                ...(item.keywords.length
                  ? [{ label: 'Keywords', value: item.keywords.join(', ') }]
                  : []),
                ...(item.repoUrl
                  ? [{ label: 'Repo URL', value: item.repoUrl }]
                  : []),
              ].map(row => (
                <Fragment key={row.label}>
                  <Typography
                    style={{
                      color: '#888',
                      fontSize: '0.82rem',
                      fontWeight: 700,
                    }}
                  >
                    {row.label}
                  </Typography>
                  <Typography
                    style={{ fontSize: '0.82rem', wordBreak: 'break-all' }}
                  >
                    {row.value}
                  </Typography>
                </Fragment>
              ))}
            </Box>
          )}
        </Box>
      </Box>
      <InstallModal
        open={installModalOpen}
        onClose={() => setInstallModalOpen(false)}
        item={item}
        backendBaseUrl={backendBaseUrl}
        onTrackDownload={(ide) => {
          if (!backendBaseUrl) return;
          fetchApi.fetch(`${backendBaseUrl}/downloads`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              artifact_name: item.name,
              artifact_type: item.type,
              ide,
              user_ref: userRef || 'anonymous',
            }),
          }).catch(() => {});
        }}
      />
      <ContributeDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        editItem={item}
      />
      <Dialog open={deleteDialogOpen} onClose={() => !deleteSubmitting && setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Marketplace Item</DialogTitle>
        <DialogContent>
          <Typography style={{ fontSize: '0.9rem' }}>
            This will create a pull request to delete <strong>{item.displayName}</strong> ({item.type}/{item.id}).
          </Typography>
          <Typography color="textSecondary" style={{ marginTop: 8, fontSize: '0.82rem' }}>
            The item will be removed only after PR review and merge.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="secondary" variant="contained" disabled={deleteSubmitting}>
            {deleteSubmitting ? 'Creating PR…' : 'Confirm Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Public export — rendered by AiMarketplacePage when query params are set ─

export function ItemDetailView() {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { items, loading, error } = useMarketplace();

  // Read the selected item from the URL so refresh and back/forward still work.
  const params = new URLSearchParams(location.search);
  const rawType = params.get('type') ?? '';
  const rawId = params.get('id') ?? '';

  // Only proceed if the type string is one we actually know about.
  const isValidType = VALID_TYPES.includes(rawType as ItemType);

  // Find by both type and id to avoid collisions across types.
  const item = isValidType
    ? items.find(
      i =>
        i.type === (rawType as ItemType) &&
        i.id === decodeURIComponent(rawId),
    )
    : undefined;

  if (loading) {
    return (
      <Box textAlign="center" pt={6}>
        <CircularProgress />
      </Box>
    );
  }

  // Distinguish a failed data fetch from a genuinely missing item.
  if (error) {
    return (
      <Box className={classes.notFound}>
        <Typography variant="h6" gutterBottom>
          Failed to load marketplace
        </Typography>
        <Typography
          color="textSecondary"
          style={{ marginBottom: 24, fontSize: '0.9rem' }}
        >
          {error}
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/ai-marketplace')}>
          Back to Marketplace
        </Button>
      </Box>
    );
  }

  // Invalid type — show a clear error rather than generic "item not found".
  if (!isValidType && rawType) {
    return (
      <Box className={classes.notFound}>
        <Typography variant="h6" gutterBottom>
          Invalid item type
        </Typography>
        <Typography
          color="textSecondary"
          style={{ marginBottom: 24, fontSize: '0.9rem' }}
        >
          "{rawType}" is not a valid marketplace type. Valid types:{' '}
          {VALID_TYPES.join(', ')}.
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/ai-marketplace')}>
          Back to Marketplace
        </Button>
      </Box>
    );
  }

  // Item not found after data has loaded — show a clear error rather than a blank page.
  if (!item) {
    return (
      <Box className={classes.notFound}>
        <Typography variant="h6" gutterBottom>
          Item not found
        </Typography>
        <Typography
          color="textSecondary"
          style={{ marginBottom: 24, fontSize: '0.9rem' }}
        >
          No {rawType || 'item'} with id "{decodeURIComponent(rawId)}" exists in
          the marketplace.
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/ai-marketplace')}>
          Back to Marketplace
        </Button>
      </Box>
    );
  }

  return <DetailBody item={item} />;
}
